import { AddItemRequestModelDto, ItemModelDto } from '@treasury/api/channel';
import { DomainEntity } from '../shared';
import { defaultItemModel } from './data/item.data';
import { RemoteDepositCustomer } from './remote-deposit-customer.entity';
import { RemoteDepositDataField } from './remote-deposit-data-field';
import { RemoteDepositLocation } from './remote-deposit-location.entity';

export class RemoteDepositItem extends DomainEntity<ItemModelDto> {
    constructor(
        dto = defaultItemModel,
        public dataFields: RemoteDepositDataField[] = [],
        public customer?: RemoteDepositCustomer
    ) {
        super(dto);
    }

    public get amount() {
        return this.dto.amount;
    }

    public set amount(amount: number) {
        this.dto.amount = amount;
    }

    public get events() {
        return this.dto.events;
    }

    public get frontImage() {
        return this.dto.frontImage;
    }

    public set frontImage(frontImage: string) {
        this.dto.frontImage = frontImage;
    }

    public get rearImage() {
        return this.dto.rearImage;
    }

    public set rearImage(rearImage: string) {
        this.dto.rearImage = rearImage;
    }

    public get customerReference() {
        return this.customer?.customerReference;
    }

    public get requiredDataFields() {
        return this.dataFields?.filter(field => field.isRequired);
    }

    public get optionalDataFields() {
        return this.dataFields?.filter(field => !field.isRequired);
    }

    public get itemReference() {
        return this.dto.itemReference ?? '';
    }

    public set itemReference(itemReference: string) {
        this.dto.itemReference = itemReference;
    }

    public get processingStatus() {
        return this.dto.processingStatus;
    }

    public get processingStatusDescription() {
        return this.dto.processingStatusDescription;
    }

    public get statusDescription() {
        return this.dto.statusDescription;
    }

    public get transactionReferenceNumber() {
        return this.dto.transactionReferenceNumber;
    }

    public get customerName() {
        if (this.customer?.firstName && this.customer?.lastName) {
            return `${this.customer.firstName} ${this.customer.lastName}`;
        }
        return undefined;
    }

    public get date() {
        const { events } = this.dto;

        if (!events || events.length === 0) {
            return new Date();
        }

        const lastEvent = events[events.length - 1];
        return new Date(lastEvent.date);
    }

    public createDefault(): this {
        return new RemoteDepositItem(defaultItemModel, []) as this;
    }

    public isValid() {
        let requiredDataFieldsValid = true;
        if (this.requiredDataFields) {
            requiredDataFieldsValid = this.requiredDataFields.every(field => field.value);
        }

        return this.amount > 0 && this.frontImage && this.rearImage && requiredDataFieldsValid;
    }

    public toAddItemRequestModelDto(location: RemoteDepositLocation): AddItemRequestModelDto {
        return {
            amount: this.amount,
            locationReference: location?.locationReference,
            customerReference: this.customer?.customerReference,
            frontImage: this.frontImage,
            rearImage: this.rearImage,
            dataFields: this.dataFields.map(field => field.toDto()),
        };
    }
}
